/**
 * Questionnaire API functions - TypeScript version
 * Authentication headers are handled automatically by the axios instance
 */

import { AxiosInstance } from "axios"
import {
  QuestionnaireGetResponse,
  QuestionnaireSaveResponse,
  QuestionnaireCheckResponse,
  QuestionnaireTranslationsGetResponse,
  QuestionnaireTranslationsSaveResponse,
  Translation,
} from "types/questionnaire.types"

/**
 * Fetches a questionnaire by ID.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param id - The project ID
 * @param owner - The owner of the project
 * @returns Promise resolving to the questionnaire string
 * @throws Will throw an error if the request fails
 */
export async function getQuestionnaire(
  axiosInstance: AxiosInstance,
  id: string,
  owner: string
): Promise<QuestionnaireGetResponse> {
  const response = await axiosInstance.get<string>(
    `/questionnaire-get?id=${id}&hint_owner=${owner}`
  )
  return response
}

/**
 * Saves a questionnaire.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param id - The project ID
 * @param qnr - The questionnaire content
 * @param owner - The owner of the project
 * @returns Promise resolving to save confirmation
 * @throws Will throw an error if the request fails
 */
export async function saveQuestionnaire(
  axiosInstance: AxiosInstance,
  id: string,
  qnr: string,
  owner: string
): Promise<QuestionnaireSaveResponse> {
  // Encode qnr into binary using TextEncoder
  const encoder = new TextEncoder()
  const binaryQnr = encoder.encode(qnr)

  const response = await axiosInstance.post(
    `/questionnaire-save?id=${id}&hint_owner=${owner}&type=qnr`,
    binaryQnr
  )
  return response
}

/**
 * Checks if a questionnaire is valid.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param id - The project ID
 * @returns Promise resolving to validation results
 * @throws Will throw an error if the request fails
 */
export async function checkQuestionnaire(
  axiosInstance: AxiosInstance,
  id: string
): Promise<QuestionnaireCheckResponse> {
  const response = await axiosInstance.post(`/questionnaire-check`, { id })
  return response
}

/**
 * Fetches a questionnaire's translations.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param id - The project ID
 * @param owner - The owner of the project
 * @returns Promise resolving to translations array
 * @throws Will throw an error if the request fails
 */
export async function getQuestionnaireTranslations(
  axiosInstance: AxiosInstance,
  id: string,
  owner: string
): Promise<QuestionnaireTranslationsGetResponse> {
  const response = await axiosInstance.get(
    `/questionnaire-translations-get?id=${id}&hint_owner=${owner}`
  )
  return response
}

/**
 * Saves questionnaire translations.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param id - The project ID
 * @param owner - The owner of the project
 * @param translations - The translations data
 * @returns Promise resolving to save confirmation
 * @throws Will throw an error if the request fails
 */
export async function saveQuestionnaireTranslations(
  axiosInstance: AxiosInstance,
  id: string,
  owner: string,
  translations: Translation[]
): Promise<QuestionnaireTranslationsSaveResponse> {
  const response = await axiosInstance.post(
    `/questionnaire-translations-save?id=${id}&hint_owner=${owner}&overwrite=true`,
    translations
  )
  return response
}
